/**
 * POTHOLE PATROL — gameplay subsystem.
 *
 * Owns: pothole entities, the repair mechanic, cone mechanics, the countdown, combo,
 * scoring, round flow and objective targeting. OWNER: `gameplay` (ARCHITECTURE.md §3).
 *
 * THE LOOP, in one sentence: the countdown is the only thing keeping you alive, and the only
 * way to buy more of it is to fill a hole. Everything else in this file exists to make that
 * exchange feel good — the combo that scales the payout, the cones that make holes findable
 * at 90 km/h, the thump that punishes you for missing one, the slalom chain that pays your
 * driving into your repairing.
 *
 * Publishes `ctx.gameplay` (GameplayQuery, §4.7) and emits the whole of the gameplay half of
 * the §5.1 event vocabulary.
 */

import * as THREE from 'three';
import { EV } from '../core/bus.js';
import { PHASE } from '../core/tunables.js';
import { clamp01 } from '../core/mathx.js';
import { TUNING } from './tuning.js';
import { RepairModel, RepairSession } from './repair.js';
import { PotholeField, POTHOLE_STATE, reliefY } from './potholes.js';
import { ConeField, CONE_STATE, CONE_ROLE } from './cones.js';
import { selectOpeningSite, selectPotholeSites } from './layout.js';
import { applyScenario, scenarioAxis, LANDMARKS } from './scenarios.js';
import {
  comboMultiplier, comboTier, repairPayout, rankFor, capRankByCompletion, starsForRank,
  makeScoreState as makeScore, resetScoreState as resetScore,
} from './scoring.js';

const PROGRESS_RING = 8;      // reused repair:progress payloads — zero steady-state garbage

export class GameplaySystem {
  static id = 'gameplay';

  constructor(ctx) {
    this.ctx = ctx;
    this.T = TUNING;

    this.rng = null;
    this.model = null;
    this.potholes = null;
    this.cones = null;
    this.session = null;

    this.phase = PHASE.TITLE;
    this.phaseTime = 0;
    this.timeRemaining = ctx.config.round.startSeconds;
    this.weather = 'thaw';
    this._weatherForced = false;
    this.scenario = 'none';
    this.roundIndex = 0;

    /**
     * Roll the opening round's weather HERE, in the constructor, not in init().
     *
     * `look` seeds its profile from `ctx.gameplay.weather` at the top of its own init()
     * (src/render/look.js), and the registry runs look BEFORE gameplay — so at that moment
     * `ctx.gameplay` is still core's fallback handle and our init() has not run. Picking in
     * the constructor and writing the answer onto the fallback is what lets look boot
     * straight into the right sky instead of flashing a frame of thaw and then fading.
     * `ctx.gameplay` is gameplay's own namespace (§4.2), so this is our field to set.
     */
    if (this._autoWeather()) {
      const boot = this._pickWeather(0);
      if (boot) {
        this.weather = boot;
        if (ctx.gameplay && ctx.gameplay.isFallback) ctx.gameplay.weather = boot;
      }
    }

    this.score = null;             // score state, see scoring.js
    this.activeTarget = null;
    this.targetId = -1;
    this.lastCompleted = null;

    this._scanTick = TUNING.target.rescanSteps;
    this._targetHold = 0;
    this._lastAbandonedId = -1;
    this._abandonCooldown = 0;
    this._comboTimer = 0;
    this._lastTier = 0;
    this.slalomChainTimer = 0;
    this._inZoneId = -1;
    this._zoneGrace = 0;
    this._completeHold = 0;
    this._sessionStartSim = 0;
    this._resultOverride = null;
    this._totalOverride = 0;
    this._gameOverSent = false;
    this._startedSent = false;
    this._replayArmed = false;
    this._frozen = false;

    // Preallocated scratch — ARCHITECTURE.md N9, nothing allocates in the step.
    this._axis = { ox: 0, oz: 0, dx: 0, dz: 1, px: 1, pz: 0 };
    this._aim = { x: 0, y: 0 };
    this._nearHoles = [];
    this._nearCones = [];
    this._progressPool = [];
    for (let i = 0; i < PROGRESS_RING; i++) {
      this._progressPool.push({ id: -1, fill01: 0, delta: 0, nozzleWorldPos: { x: 0, y: 0, z: 0 }, hitCell: -1 });
    }
    this._progressCursor = 0;
    this._nozzle = new THREE.Vector3();
    this._tmp = new THREE.Vector3();

    this.reticle = null;
    this.targetRing = null;
    this.query = null;
    this._onCollided = null;
  }

  // ================================================================== boot

  async init() {
    const ctx = this.ctx;
    this.rng = ctx.rng.fork('gameplay');

    // The constructor already chose this round's weather so look could boot into it. Publish
    // the fact now that everyone has a bus to hear it on: the emit is queued and dispatched
    // at the end of the first fixed step (§5), by which point fx and audio have subscribed.
    // 0 ms because there is nothing to fade FROM at boot.
    if (this._autoWeather()) {
      ctx.bus.emit(EV.WORLD_WEATHER_CHANGED, { weather: this.weather, transitionMs: 0 });
      ctx.audio.setAmbience(this.weather, 1);
    }

    this.model = new RepairModel(this.T.repair);
    this.session = new RepairSession(this.model.grid);
    this.score = makeScore();

    this.potholes = new PotholeField(ctx, this.T.pothole, this.T.visual, this.model);
    this.potholes.build();
    this.cones = new ConeField(ctx, this.T.cone, this.T.slalom);
    this.cones.build();

    this._buildMarkers();

    this._publishQuery();

    // A crash breaks the chain. We never listen for our OWN events (ARCHITECTURE.md §5) —
    // only for other subsystems' facts.
    this._onCollided = (p) => this._onVehicleCollided(p);
    ctx.bus.on(EV.VEHICLE_COLLIDED, this._onCollided);

    this.layoutRound();
    this._setPhase(PHASE.TITLE, true);
  }

  /** ctx.gameplay — GameplayQuery, ARCHITECTURE.md §4.7. Live getters, never a copy. */
  _publishQuery() {
    const self = this;
    this.query = {
      get timeRemaining() { return self.timeRemaining; },
      get score() { return self.score.score; },
      get cash() { return self.score.cash; },
      get combo() { return self.score.combo; },
      get multiplier() { return self.score.multiplier; },
      get repaired() { return self.score.repaired; },
      get total() { return self._totalOverride || self.potholes.count; },
      get activeTarget() { return self.activeTarget; },
      get repairSession() { return self.session.active ? self._sessionView : null; },
      get phase() { return self.phase; },
      get weather() { return self.weather; },
      get roundIndex() { return self.roundIndex; },
      get slalomChain() { return self.score.slalomChain; },
      get bestCombo() { return self.score.bestCombo; },
      get perfects() { return self.score.perfects; },
      potholes() { return self.potholes.live(); },
      cones() { return self.cones.live(); },
      /** Cell grid geometry, so the HUD can draw the same cells the player is filling. */
      cellGrid: this.model.grid,
    };

    this._sessionView = {
      get potholeId() { return self.session.potholeId; },
      get fill01() { return self.session.fill01; },
      get quality01() { return self.model.quality(self.session); },
      get cells() { return self.session.cells; },
      get active() { return self.session.active; },
      get minCell() { return self.session.minCell; },
      get assisting() { return self.session.assisting; },
      get focus() { return self.session.focus; },
      get aimX() { return self.session.aimX; },
      get aimY() { return self.session.aimY; },
    };

    this.ctx.gameplay = this.query;
  }

  /** Nozzle aim reticle and the ground ring around the active objective. */
  _buildMarkers() {
    const ctx = this.ctx;
    const v = this.T.visual;

    // Road PAINT, not a particle sprite. `fx.spray` and `sign.emissive` are sprite/atlas
    // mapped, and a flat ring sampling a sprite's corner texel is an invisible ring.
    // `road.markings` is white line paint: transparent, depth-write off, polygon-offset
    // already set for exactly this job, and semantically right — these are painted marks on
    // a road, which is what a Ville de Montréal work zone actually puts down.
    const retGeo = annulus(1 - v.reticleWidth, 1, v.reticleSegments);
    const retMat = ctx.materials.instanceOf('road.markings');
    // 0.92 → 0.42 → 0.07. See TUNING.visual.reticleOpacity: at 0.42 WITH the emissive lift
    // the ring measured 0.209 linear on a 0.017 crater and read as a debug gizmo. It is
    // painted guidance, not geometry: it sits UNDER the debris and must never out-contrast
    // the patch. (Two critics filed this under `ui` and `look`; it is gameplay's mesh.)
    softMarkerMaterial(retMat, v.reticleOpacity, v.reticleEmissive);
    this.reticle = new THREE.Mesh(retGeo, retMat);
    this.reticle.name = 'gameplay.nozzleReticle';
    this.reticle.frustumCulled = false;
    this.reticle.renderOrder = 6;
    this.reticle.visible = false;
    ctx.scene.add(this.reticle);

    const rw = this.T.target.ringWidth * 0.5;
    const ringGeo = annulus(1 - rw, 1 + rw, 48);
    const ringMat = ctx.materials.instanceOf('road.markings.yellow');
    softMarkerMaterial(ringMat, this.T.visual.targetRingOpacity, this.T.visual.targetRingEmissive);
    this.targetRing = new THREE.Mesh(ringGeo, ringMat);
    this.targetRing.name = 'gameplay.targetRing';
    this.targetRing.frustumCulled = false;
    this.targetRing.renderOrder = 5;
    this.targetRing.visible = false;
    ctx.scene.add(this.targetRing);
  }

  // ================================================================== round setup

  /**
   * Scatter the round's potholes along the street graph and ring every one with cones.
   * The opening hole is placed deliberately close so the loop starts inside two seconds
   * rather than after a hunt — Crazy Taxi always puts the first fare in front of you.
   */
  layoutRound() {
    const ctx = this.ctx;
    const T = this.T;
    const layoutLabel = `layout:v${T.layout.version}:${this.weather}:round:${this.roundIndex}`;
    const rng = this.rng.fork(layoutLabel);
    // Site choice and pothole cosmetics are independent streams: adding a crack or cone RNG
    // draw must never reshuffle the work district for an existing seed and round.
    const siteRng = rng.fork('sites');
    const detailRng = rng.fork('details');

    this.clearField();

    const axis = scenarioAxis(ctx, this._axis);
    const parked = !!(ctx.player && ctx.player.isFallback);
    const opening = parked ? T.pothole.openingDistanceParked : T.pothole.openingDistance;
    const candidates = ctx.world.spawnPoints('pothole');
    const desiredOpening = {
      x: axis.ox + axis.dx * opening,
      z: axis.oz + axis.dz * opening,
    };
    const openingRoad = ctx.world.roadAt(desiredOpening.x, desiredOpening.z);
    // Keep the tutorial hole exactly ahead when that point is already legal road. Replays
    // can end at an oblique pose, so only an off-road desired point needs candidate snapping.
    const openingSite = openingRoad && openingRoad.onRoad ? null : selectOpeningSite(
      candidates, desiredOpening, { x: axis.ox, z: axis.oz },
      { x: axis.dx, z: axis.dz }, T.layout,
    );
    const openingX = openingSite ? openingSite.pos.x : desiredOpening.x;
    const openingZ = openingSite ? openingSite.pos.z : desiredOpening.z;

    const first = this.potholes.spawn(
      openingX, openingZ, detailRng, this.weather,
      { radius: T.pothole.radiusMax * 0.92, depth: T.pothole.depthMax * 0.9, coneInHole: false }
    );
    if (first) {
      first.name = this._landmarkFor(first.position.x, first.position.z, detailRng);
      this.cones.ringPothole(first, detailRng);
    }

    // Everything else is sampled from the complete world pool. The selector owns hard
    // spacing, compact-district connectivity and street/axis balance; source order is never
    // a gameplay priority.
    const wanted = T.pothole.targetCount;
    const openingRef = openingSite || {
      pos: first ? first.position : desiredOpening,
      dir: openingRoad && openingRoad.dir ? openingRoad.dir : { x: axis.dx, y: axis.dz },
    };
    const sites = selectPotholeSites(
      candidates, openingRef, Math.max(0, wanted - this.potholes.count), siteRng, T.layout,
    );

    for (const sp of sites) {
      if (this.potholes.count >= T.pothole.capacity - 2) break;
      const x = sp.pos.x, z = sp.pos.z;
      const p = this.potholes.spawn(x, z, detailRng, this.weather);
      if (!p) break;
      p.name = this._landmarkFor(x, z, detailRng);
      this.cones.ringPothole(p, detailRng);
    }

    // Slalom corridors along the road, offset from the holes.
    const s = T.slalom;
    for (let r = 0; r < s.runsPerRound; r++) {
      const along = 60 + r * 95;
      this.cones.layRun(
        axis.ox + axis.dx * along + axis.px * (r % 2 ? 1.7 : -1.7),
        axis.oz + axis.dz * along + axis.pz * (r % 2 ? 1.7 : -1.7),
        axis.dx, axis.dz, s.gatesPerRun, detailRng
      );
    }

    // The deliberately nearby legal road site teaches the loop; the generic heading-biased
    // picker takes over after it is repaired.
    if (first) this._setTarget(first);
    else this.retargetNow();
  }

  _landmarkFor(x, z, rng) {
    const lm = this.ctx.world.landmarkNear(x, z);
    if (lm && lm.name && !this.ctx.world.isFallback) return lm.name;
    return LANDMARKS[rng.int(0, LANDMARKS.length)];
  }

  clearField() {
    this.endSession('reset', false);
    this.potholes.clear();
    this.cones.clear();
    this.activeTarget = null;
    this.targetId = -1;
    this._targetHold = 0;
    this._lastAbandonedId = -1;
    this._abandonCooldown = 0;
    this.lastCompleted = null;
    this._inZoneId = -1;
    this._nearHoles.length = 0;
    this._nearCones.length = 0;
  }

  /** Take a pothole's marker ring down — it has been repaired, the ring is not needed. */
  retireRing(p) {
    if (p.ringConeStart < 0) return;
    for (let i = p.ringConeStart; i < p.ringConeStart + p.ringConeCount; i++) {
      const c = this.cones.byId(i);
      if (c && c.potholeId === p.id && c.role === CONE_ROLE.RING) c.active = false;
    }
    p.ringConeCount = 0;
    p.ringConeStart = -1;
    this.cones.markDirty();
  }

  // ================================================================== simulation

  fixedUpdate(dt) {
    switch (this.phase) {
      case PHASE.TITLE: this._stepTitle(dt); break;
      case PHASE.COUNTDOWN: this._stepCountdown(dt); break;
      case PHASE.PLAYING: this._stepPlaying(dt); break;
      case PHASE.TIMEUP: this._stepTimeup(dt); break;
      case PHASE.RESULT: this._stepResult(dt); break;
      default: this.phaseTime += dt; break;
    }
    // See update(): gameplay is not in core's UPDATE_ORDER, so the mesh sync has to be
    // reachable from here or every pothole and cone stays frozen at its build-time state.
    this._syncVisuals();
  }

  _stepTitle(dt) {
    this.phaseTime += dt;
    if (this.ctx.input.anyPressed || this.phaseTime >= this.T.round.titleAutoStart) {
      this._setPhase(PHASE.COUNTDOWN);
    }
  }

  _stepCountdown(dt) {
    this.phaseTime += dt;
    const len = this.ctx.config.round.countdownSeconds || this.T.round.countdownSeconds;
    if (this.phaseTime >= len) this._setPhase(PHASE.PLAYING);
  }

  _stepTimeup(dt) {
    this.phaseTime += dt;
    // Cones still fall over during the TIME UP beat — the world does not freeze on the buzzer.
    this.cones.stepPhysics(dt);
    if (this.phaseTime >= this.T.round.timeupHold) this._setPhase(PHASE.RESULT);
  }

  _stepResult(dt) {
    this.phaseTime += dt;
    // Harness result shots are pinned states, not interactive rounds. Drivers also report
    // anyPressed continuously, so the advertised replay control is specifically Space/A.
    if (this.ctx.isHarness || this.phaseTime < this.T.round.replayDelay) return;
    const held = this.ctx.input.handbrake > 0.5;
    if (!held) { this._replayArmed = true; return; }
    if (!this._replayArmed) return;
    this._replayArmed = false;
    this._startNextRound();
  }

  _startNextRound() {
    this.roundIndex++;
    // Before layoutRound(), which keys its site selection on the weather.
    this._rollRoundWeather(this.T.weather.transitionMs);
    this.session = new RepairSession(this.model.grid);
    resetScore(this.score);
    this.timeRemaining = this.ctx.config.round.startSeconds;
    this.scenario = 'none';
    this._comboTimer = 0;
    this._lastTier = 0;
    this.slalomChainTimer = 0;
    this._zoneGrace = 0;
    this._completeHold = 0;
    this._sessionStartSim = 0;
    this._resultOverride = null;
    this._totalOverride = 0;
    this._gameOverSent = false;
    this._startedSent = false;
    this._scanTick = this.T.target.rescanSteps;
    this._progressCursor = 0;
    this.layoutRound();
    this._setPhase(PHASE.COUNTDOWN);
  }

  _stepPlaying(dt) {
    this.phaseTime += dt;

    this._stepTimer(dt);
    if (this.phase !== PHASE.PLAYING) return;

    // Counted here rather than inside _rescan(): retargetNow() can fire off-cadence, so the
    // rescan is not a clock.
    if (this._targetHold > 0) this._targetHold -= dt;
    if (this._abandonCooldown > 0) this._abandonCooldown -= dt;
    if (++this._scanTick >= this.T.target.rescanSteps) { this._scanTick = 0; this._rescan(); }

    this._stepPatches(dt);
    this._stepThump(dt);
    this._stepConeContacts(dt);
    this.cones.stepPhysics(dt);
    this._stepGates(dt);
    this._stepZone(dt);
    this._stepRepair(dt);
    this._stepComboWindow(dt);
  }

  // ------------------------------------------------------------------ timer

  _stepTimer(dt) {
    const prev = this.timeRemaining;
    this.timeRemaining = Math.max(this.T.round.timerFloor, this.timeRemaining - dt);

    const marks = this.ctx.config.round.warnAt;
    for (let i = 0; i < marks.length; i++) {
      const m = marks[i];
      if (prev > m && this.timeRemaining <= m) {
        this.ctx.bus.emit(EV.GAME_TIME_WARNING, { secondsLeft: m });
        this.ctx.audio.sfx('timeWarning', { gain: m <= 5 ? 1 : 0.7 });
      }
    }

    if (this.timeRemaining <= 0) {
      if (this.session.active) this.endSession('timeup', true);
      this._breakCombo('timeout');
      this._setPhase(PHASE.TIMEUP);
    }
  }

  // ------------------------------------------------------------------ targeting

  /**
   * Rebuild the near lists and pick the objective. Runs at 10 Hz — a target that re-elects
   * itself every frame flickers the chevron and reads as noise.
   *
   * `force` re-elects from scratch, ignoring stickiness. That is the retargetNow() path: the
   * round has just been laid, a repair has just completed, a patch has just been wrecked, or
   * a scenario has teleported the player. In every one of those the incumbent is stale by
   * construction, and the scenarios in particular rely on retargetNow() naming the nearest
   * hole to wherever they just put the truck.
   */
  _rescan(force) {
    const ctx = this.ctx;
    const pos = ctx.player.position;
    const px = pos.x, pz = pos.z;
    const fx = ctx.player.forward ? ctx.player.forward.x : 0;
    const fz = ctx.player.forward ? ctx.player.forward.z : 1;
    const T = this.T.target;

    this._nearHoles.length = 0;
    let bestCost = Infinity;
    let best = null;
    // The incumbent is scored on exactly the same curve as its challengers, so the comparison
    // below is a ratio of like for like rather than raw metres against heading-biased cost.
    let curCost = Infinity;
    let curDot = 1;

    for (let i = 0; i < this.potholes.count; i++) {
      const p = this.potholes.items[i];
      if (!p.active) continue;
      const dx = p.position.x - px, dz = p.position.z - pz;
      const d2 = dx * dx + dz * dz;
      const d = Math.sqrt(d2);
      p.distance = d;
      if (d < 34) this._nearHoles.push(p);
      if (!p.isOpen) continue;

      // Bias toward whatever is in front of you — turning round for a hole behind the truck
      // is never the fun choice.
      const inv = d > 1e-3 ? 1 / d : 0;
      const dot = (dx * fx + dz * fz) * inv;
      const cost = d * (1 + T.forwardBias * (1 - dot));
      if (p.id === this.targetId) { curCost = cost; curDot = dot; }
      if (cost < bestCost) { bestCost = cost; best = p; }
    }

    this._nearCones.length = 0;
    for (let i = 0; i < this.cones.count; i++) {
      const c = this.cones.items[i];
      if (!c.active) continue;
      const dx = c.position.x - px, dz = c.position.z - pz;
      if (dx * dx + dz * dz < 144) this._nearCones.push(c);
    }

    if (!best) {
      if (this.targetId >= 0) { this.targetId = -1; this.activeTarget = null; this._targetHold = 0; }
      return;
    }

    const cur = this.potholes.byId(this.targetId);
    if (force || !cur || !cur.active || !cur.isOpen || cur === best) { this._setTarget(best); return; }

    // Five conditions, all required. See TUNING.target for why a ratio, a clock and a memory
    // of the last abandonment replaced the old 4 m hysteresis.
    const bouncingBack = best.id === this._lastAbandonedId && this._abandonCooldown > 0;
    const abandoned = !bouncingBack &&
      this._targetHold <= 0 &&
      curDot < T.abandonDot &&
      cur.distance > T.abandonMinDistance &&
      bestCost < curCost * T.switchCostRatio;
    if (abandoned) {
      this._lastAbandonedId = cur.id;
      this._abandonCooldown = T.reElectCooldown;
      this._setTarget(best);
    } else {
      this._refreshTargetDistance(cur);
    }
  }

  _setTarget(p) {
    if (!p) { this.targetId = -1; this.activeTarget = null; return; }
    const changed = this.targetId !== p.id;
    this.targetId = p.id;
    if (!this.activeTarget) this.activeTarget = { id: p.id, position: new THREE.Vector3(), name: '', distance: 0 };
    this.activeTarget.id = p.id;
    this.activeTarget.position.copy(p.position);
    this.activeTarget.name = p.name;
    this.activeTarget.distance = p.distance;
    if (changed) {
      this._targetHold = this.T.target.minHoldSeconds;
      this.ctx.bus.emit(EV.POTHOLE_TARGETED, {
        id: p.id, position: p.position, name: p.name, distance: p.distance,
      });
    }
  }

  _refreshTargetDistance(p) {
    if (this.activeTarget) this.activeTarget.distance = p.distance;
  }

  retargetNow() { this._scanTick = this.T.target.rescanSteps; this._rescan(true); }

  forceTarget(p) { this._setTarget(p); }

  // ------------------------------------------------------------------ patches

  _stepPatches(dt) {
    const T = this.T.pothole;
    const ctx = this.ctx;
    const px = ctx.player.position.x, pz = ctx.player.position.z;
    const speed = ctx.player.speed || 0;

    // Every hole, not just the nearby ones: a patch you laid and drove away from still has
    // to cure, and traffic still has to be able to wreck it behind your back.
    for (let i = 0; i < this.potholes.count; i++) {
      const p = this.potholes.items[i];
      if (!p.active) continue;
      if (p.thumpCooldown > 0) p.thumpCooldown -= dt;

      if (p.state !== POTHOLE_STATE.FRESH) continue;

      p.patchAge += dt;
      p.visualDirty = true;

      if (p.patchAge >= T.cureSeconds) {
        p.state = POTHOLE_STATE.CURED;
        p.cured = true;
        ctx.bus.emit(EV.PATCH_CURED, { id: p.id, position: p.position });
        ctx.audio.sfx('patchCured', { pos: p.position, gain: 0.6 });
        continue;
      }

      // You can wreck your own work by driving across it before it sets.
      const dx = p.position.x - px, dz = p.position.z - pz;
      if (speed > T.playerRuinSpeed && dx * dx + dz * dz < p.radius * p.radius) {
        this._ruinPatch(p, 'player');
        continue;
      }

      // Traffic. `gameplay` has no positional query onto the traffic system (there is no
      // ctx.traffic in §4.2), so an unguarded fresh patch carries a per-second hazard drawn
      // from the seeded fork. Deterministic, and it only ever fires when the player has
      // knocked their own guard cone away — which is exactly the tension we want.
      if (!this._patchGuarded(p) && this.rng.next() < T.trafficRuinChancePerSec * dt) {
        this._ruinPatch(p, 'traffic');
      }
    }
  }

  _patchGuarded(p) {
    if (p.guardConeId < 0) return false;
    const c = this.cones.byId(p.guardConeId);
    return !!(c && c.active && c.state === CONE_STATE.STANDING);
  }

  _ruinPatch(p, by) {
    this.potholes.ruin(p);
    this.score.ruined++;
    if (this.score.repaired > 0) this.score.repaired--;
    this.ctx.bus.emit(EV.PATCH_RUINED, { id: p.id, position: p.position, by });
    this.ctx.bus.emit(EV.REPAIR_FAILED, { id: p.id, reason: 'ruined' });
    this.ctx.fx.shake(0.25, 0.25);
    this.ctx.audio.sfx('patchRuined', { pos: p.position });
    this.announce('PATCH WRECKED', 'ruined', 1400);
    this.cones.ringPothole(p, this.rng);
    this.retargetNow();
  }

  // ------------------------------------------------------------------ the thump

  /**
   * THE THUMP. Driving into an open hole has to be a real punishment you feel in the seat —
   * it is the running joke made mechanical, and it is what makes the cone rings worth reading.
   */
  _stepThump(dt) {
    const ctx = this.ctx;
    const T = this.T.thump;
    const speed = ctx.player.speed || 0;
    if (speed < T.minSpeed) return;

    const px = ctx.player.position.x, pz = ctx.player.position.z;
    for (let i = 0; i < this._nearHoles.length; i++) {
      const p = this._nearHoles[i];
      if (!p.isOpen || p.thumpCooldown > 0) continue;
      const dx = p.position.x - px, dz = p.position.z - pz;
      const r = p.radius * 0.95;
      if (dx * dx + dz * dz > r * r) continue;

      p.thumpCooldown = T.cooldown;
      const sizeK = 0.5 + 0.5 * (p.radius / this.T.pothole.radiusMax) * (p.depth / this.T.pothole.depthMax);
      const severity = clamp01(speed / T.speedForFullSeverity) * clamp01(sizeK * 1.4) * (1 - p.fill01 * 0.6);

      ctx.bus.emit(EV.POTHOLE_THUMPED, { id: p.id, position: p.position, speed, severity });
      ctx.fx.shake(T.shakeBase + T.shakeSpan * severity, T.shakeDuration);
      ctx.fx.freezeFrame(Math.round(T.freezeMs * severity));
      ctx.fx.debris(p.position, ctx.player.velocity, 'asphalt');
      ctx.audio.sfx('thump', { pos: p.position, gain: 0.6 + 0.4 * severity, rate: 1 - severity * 0.2 });

      this.score.thumps++;
      this.score.score = Math.max(0, this.score.score - this.T.payout.thumpScorePenalty);
      this._breakCombo('thump');
      this.announce('NID-DE-POULE!', 'thump', 1200);

      if (this.session.active && this.session.potholeId === p.id) this.endSession('thump', true);
    }
  }

  // ------------------------------------------------------------------ cones

  _stepConeContacts(dt) {
    const ctx = this.ctx;
    const T = this.T.cone;
    const speed = ctx.player.speed || 0;
    if (speed < 0.4) return;

    const px = ctx.player.position.x, pz = ctx.player.position.z;
    const vx = ctx.player.velocity ? ctx.player.velocity.x : 0;
    const vz = ctx.player.velocity ? ctx.player.velocity.z : 0;
    const inv = speed > 1e-3 ? 1 / speed : 0;
    const dirX = vx * inv, dirZ = vz * inv;
    const r2 = T.playerRadius * T.playerRadius;
    const sim = ctx.time.sim;

    for (let i = 0; i < this._nearCones.length; i++) {
      const c = this._nearCones[i];
      if (!c.active || c.moving) continue;
      const dx = c.position.x - px, dz = c.position.z - pz;
      if (dx * dx + dz * dz > r2) continue;
      if (sim - c.touchedAt < 0.25) continue;

      const wasStanding = this.cones.knock(c, dirX, dirZ, speed, sim);
      const launched = speed >= T.launchSpeed;
      const impulse = speed * T.impulseScale;

      ctx.bus.emit(EV.CONE_HIT, { id: c.id, position: c.position, impulse, launched });
      ctx.fx.debris(c.position, ctx.player.velocity, 'cone');
      ctx.audio.sfx('coneHit', { pos: c.position, gain: clamp01(0.35 + speed * 0.05), rate: launched ? 1.15 : 1 });
      this.score.conesHit++;

      if (!wasStanding) continue;

      if (c.role === CONE_ROLE.GATE) this._breakSlalom(c.position);
      if (c.role === CONE_ROLE.DEPLOYED) {
        const p = this.potholes.byId(c.potholeId);
        if (p) p.guardConeId = -1;    // your fresh patch is now exposed to traffic
      }
    }
  }

  /**
   * Slalom gates. Crossing the gate plane between the two cones with neither of them recently
   * touched chains the multiplier; touching one breaks it. The chain is banked against your
   * NEXT repair, which is what welds the driving to the repairing.
   */
  _stepGates(dt) {
    const ctx = this.ctx;
    const s = this.T.slalom;
    const px = ctx.player.position.x, pz = ctx.player.position.z;
    const sim = ctx.time.sim;

    for (let i = 0; i < this.cones.gateCount; i++) {
      const g = this.cones.gates[i];
      if (!g.active) continue;

      const rx = px - g.cx, rz = pz - g.cz;
      const along = rx * g.nx + rz * g.nz;
      if (along < -60 || along > 60) { g.side = 0; continue; }

      const side = along >= 0 ? 1 : -1;
      const prev = g.side;
      g.side = side;
      if (prev === 0 || prev === side) continue;

      const across = rx * g.px + rz * g.pz;
      if (Math.abs(across) > g.half * s.passHalfWidthFactor) continue;

      const a = this.cones.byId(g.coneA);
      const b = this.cones.byId(g.coneB);
      const clean =
        a && b && a.active && b.active &&
        a.state === CONE_STATE.STANDING && b.state === CONE_STATE.STANDING &&
        !this.cones.recentlyTouched(a, sim) && !this.cones.recentlyTouched(b, sim);

      if (!clean) { this._breakSlalom(ctx.player.position); continue; }

      this.score.slalomChain++;
      if (this.score.slalomChain > this.score.bestSlalom) this.score.bestSlalom = this.score.slalomChain;
      this.slalomChainTimer = 0;

      const mult = 1 + Math.min(this.score.slalomChain, s.chainCap) * s.perLink;
      this.score.score += this.T.payout.slalomLinkScoreBonus * this.score.slalomChain;

      ctx.bus.emit(EV.CONE_SLALOM_CHAINED, {
        chain: this.score.slalomChain, multiplier: mult, position: ctx.player.position,
      });
      /**
       * ANCHOR IT ON THE GATE, NOT ON THE TRUCK. §4.8b's second trap, verbatim: anything
       * placed at `ctx.player.position` sits inside a 7.6 m body and is occluded by it from
       * the chase camera. In the round-10 `cone-slalom` frame this string survived as the
       * two characters "X1" floating beside the cab — a critic read it as a broken callout,
       * and it was: "SLALOM ×11" with everything but its tail hidden behind the vehicle.
       * The gate you just threaded is on the open road and is also the more informative place
       * to put it.
       */
      this._tmp.set(g.cx, ctx.player.position.y + 0.9, g.cz);
      ctx.fx.popText(this._tmp, `SLALOM ×${this.score.slalomChain}`, 'slalom');
      ctx.audio.sfx('slalomChain', { rate: 1 + Math.min(this.score.slalomChain, 12) * 0.045 });
      if (this.score.slalomChain === 3 || this.score.slalomChain === 6 || this.score.slalomChain === 10) {
        ctx.audio.announcer('slalom', 1);
        this.announce(`SLALOM ×${this.score.slalomChain}`, 'slalom', 1200);
      }
    }

    if (this.score.slalomChain > 0) {
      this.slalomChainTimer += dt;
      if (this.slalomChainTimer > s.chainWindow) this._breakSlalom(ctx.player.position);
    }
  }

  _breakSlalom(position) {
    if (this.score.slalomChain <= 0) return;
    const chain = this.score.slalomChain;
    this.score.slalomChain = 0;
    this.slalomChainTimer = 0;
    this.ctx.bus.emit(EV.CONE_SLALOM_BROKEN, { chain, position });
    this.ctx.audio.sfx('slalomBreak', { gain: 0.5 });
    // Showboating and then blowing it costs the repair chain too.
    if (chain >= this.T.combo.slalomShowboatBreak) this._breakCombo('coneHit');
  }

  // ------------------------------------------------------------------ repair zone + session

  /**
   * Zone tracking. Entering the work zone of an open hole is a fact worth broadcasting on its
   * own — the UI swaps to the repair prompt, audio ducks — separately from actually pulling
   * the trigger.
   */
  _stepZone(dt) {
    const ctx = this.ctx;
    const T = this.T.repair;
    const px = ctx.player.position.x, pz = ctx.player.position.z;

    let inside = null;
    let bestD = Infinity;
    for (let i = 0; i < this._nearHoles.length; i++) {
      const p = this._nearHoles[i];
      if (!p.isOpen) continue;
      const dx = p.position.x - px, dz = p.position.z - pz;
      const d = Math.sqrt(dx * dx + dz * dz);
      const pad = p.radius + T.zonePad + (this._inZoneId === p.id ? T.exitHysteresis : 0);
      if (d <= pad && d < bestD) { bestD = d; inside = p; }
    }

    const id = inside ? inside.id : -1;
    if (id !== this._inZoneId) {
      if (this._inZoneId >= 0) {
        const prev = this.potholes.byId(this._inZoneId);
        if (prev) ctx.bus.emit(EV.POTHOLE_EXITED, { id: prev.id, fill01: prev.fill01 });
      }
      this._inZoneId = id;
      if (inside) {
        const speed = ctx.player.speed || 0;
        ctx.bus.emit(EV.POTHOLE_ENTERED, { id: inside.id, position: inside.position, approachSpeed: speed });
        this._checkThread(inside, speed);
      }
    }

    /**
     * Session lifetime, tracked separately from the zone id and with a grace window. Binning
     * a half-finished repair the instant the truck rolls a metre too far is miserable and
     * reads as a bug; you get exitGrace seconds to get back before it actually fails.
     */
    if (!this.session.active || this._completeHold > 0) { this._zoneGrace = T.exitGrace; return; }
    const held = this.potholes.byId(this.session.potholeId);
    if (!held) { this.endSession('left', true); return; }
    const hx = held.position.x - px, hz = held.position.z - pz;
    const hd = Math.sqrt(hx * hx + hz * hz);
    if (hd <= held.radius + T.zonePad + T.exitHysteresis) {
      this._zoneGrace = T.exitGrace;
    } else {
      this._zoneGrace -= dt;
      if (this._zoneGrace <= 0) this.endSession('left', true);
    }
  }

  /**
   * THREADING THE NEEDLE. You got into the work zone through the marker ring without
   * knocking a single cone over. At speed that is a genuinely difficult thing to do and it
   * deserves to be called out.
   */
  _checkThread(p, speed) {
    if (speed < 6) return;
    if (p.ringConeStart < 0 || p.ringConeCount < 2) return;
    const sim = this.ctx.time.sim;
    for (let i = p.ringConeStart; i < p.ringConeStart + p.ringConeCount; i++) {
      const c = this.cones.byId(i);
      if (!c || !c.active) continue;
      if (c.state !== CONE_STATE.STANDING) return;
      if (this.cones.recentlyTouched(c, sim)) return;
    }
    this.score.threads++;
    this.score.score += this.T.payout.threadScoreBonus;
    this.ctx.bus.emit(EV.CONE_THREADED, { gateId: p.id, position: p.position, chain: this.score.threads });
    this.ctx.fx.popText(p.position, 'THREADING THE NEEDLE', 'thread');
    this.ctx.audio.announcer('thread', 1);
    this.announce('THREADING THE NEEDLE', 'thread', 1500);
  }

  _stepRepair(dt) {
    const ctx = this.ctx;
    const T = this.T.repair;
    const trigger = ctx.input.trigger || 0;
    const speed = ctx.player.speed || 0;

    // The finished session is held on screen at 100% for a beat before it clears.
    if (this._completeHold > 0) {
      this._completeHold -= dt;
      if (this._completeHold <= 0) {
        this.session.release();
        this._completeHold = 0;
      }
      return;
    }

    if (!this.session.active) {
      if (trigger < T.triggerOn) return;
      if (speed > T.engageSpeed) return;
      const p = this.potholes.byId(this._inZoneId);
      if (!p || !p.isOpen) return;
      this.beginSession(p, false);
      return;
    }

    const p = this.potholes.byId(this.session.potholeId);
    if (!p || !p.active || !p.isOpen) { this.endSession('left', true); return; }

    if (this.session.pinned) {
      /**
       * Harness is holding the meter for a shot. The bar still sweeps and the fill cannot
       * advance — but the per-step event must carry the delta a LIVE repair at this fill
       * would be delivering, not zero. Every downstream intensity is scaled by it
       * (`fx` sprays at 0.55 + delta x 26 and splatters at a floor + delta x 8), so reporting
       * zero pinned the hero shot's jet at its weakest possible setting and left fx's ejecta
       * as the loudest thing in the frame. `probeDelta` sprays, reads, and puts it all back.
       */
      this.model.step(this.session, dt, ctx.input.nozzle.x, ctx.input.nozzle.y, 1, this._aim);
      this._emitProgress(p, this.model.probeDelta(this.session, dt, this._rateMulFor(p)));
      return;
    }

    if (trigger < T.triggerOff) return;      // paused, but you keep the hole

    const done = this.model.step(this.session, dt, ctx.input.nozzle.x, ctx.input.nozzle.y, this._rateMulFor(p), this._aim);

    p.fill01 = this.session.fill01;
    p.visualDirty = true;

    this._emitProgress(p, this.session.lastDelta);

    const fresh = this.model.newMilestones(this.session);
    if (fresh) {
      const ms = T.milestones;
      for (let i = 0; i < ms.length; i++) {
        if (fresh & (1 << i)) {
          ctx.bus.emit(EV.REPAIR_MILESTONE, { id: p.id, pct: ms[i] });
          ctx.audio.sfx('repairMilestone', { pos: p.position, rate: 1 + i * 0.09 });
        }
      }
    }

    if (done) this.completeRepair(p);
  }

  /**
   * Spray rate multiplier for this hole: weather, and how much asphalt the hole actually
   * holds. The cell grid is normalised, so without the second term a 2.6 m crater and a 1.3 m
   * one were the same job — see TUNING.pothole.volumeExponent.
   */
  _rateMulFor(p) {
    const T = this.T.repair, P = this.T.pothole;
    const w = T.weatherRate[this.weather] !== undefined ? T.weatherRate[this.weather] : 1;
    return w * this.model.volumeRate(p.radius / P.radiusRef, P);
  }

  /** Where the hot asphalt is actually landing, in world space. */
  _nozzleWorld(p, out) {
    const T = this.T.pothole;
    const ax = this.session.aimX, ay = this.session.aimY;
    const u = clamp01(Math.sqrt(ax * ax + ay * ay));
    const cell = this.session.hitCell;
    const f = cell >= 0 && this.session.cells ? this.session.cells[cell] : 0;
    const y0 = reliefY(u, T);
    out.set(p.position.x + ax * p.radius, p.groundY + y0 + (T.patchRise - y0) * f, p.position.z + ay * p.radius);
    return out;
  }

  /**
   * repair:progress every fixed step while filling, so `fx` can put spray particles exactly
   * where the asphalt lands and `audio` can modulate the nozzle. Payloads come from a small
   * ring so this costs nothing in steady state.
   */
  _emitProgress(p, delta) {
    const n = this._nozzleWorld(p, this._nozzle);
    const payload = this._progressPool[this._progressCursor];
    this._progressCursor = (this._progressCursor + 1) % PROGRESS_RING;
    payload.id = p.id;
    payload.fill01 = this.session.fill01;
    payload.delta = delta;
    payload.hitCell = this.session.hitCell;
    payload.nozzleWorldPos.x = n.x;
    payload.nozzleWorldPos.y = n.y;
    payload.nozzleWorldPos.z = n.z;
    this.ctx.bus.emit(EV.REPAIR_PROGRESS, payload);
    /**
     * NO DIRECT fx CALLS HERE. `fx` already drives both the jet and the splatter off
     * `repair:progress` — and it THROTTLES the splatter (`FX.splatter.progressEvery`) because
     * it fires sixty times a second. We were emitting the event AND calling `fx.spray` and
     * `fx.splatter` on top of it, unthrottled, so the effect ran at roughly double intensity
     * with an unthrottled ejecta burst every single step.
     *
     * It was invisible until round 12 only because the harness-pinned shot reported `delta` as
     * zero and the `delta > 0` guard suppressed the duplicate; the moment the pinned delta
     * became truthful, `pothole-fill` filled with flying chunks. In live play it had been
     * double-driving the whole time. §5: events are how other subsystems find out, and one
     * signal is the whole point.
     */
  }

  beginSession(p, pinned) {
    const ctx = this.ctx;
    this.session.bind(p.id, p.cells);
    this.session.pinned = !!pinned;
    p.state = POTHOLE_STATE.REPAIRING;
    p.visualDirty = true;
    this._sessionStartSim = ctx.time.sim;
    this._inZoneId = p.id;
    ctx.bus.emit(EV.REPAIR_STARTED, { id: p.id, position: p.position, cells: this.model.grid.count });
    ctx.audio.sfx('repairStart', { pos: p.position });
  }

  /** Hold the meter at a fixed level for a shot. */
  pinSession(fill01) {
    const p = this.potholes.byId(this.session.potholeId);
    if (!p) return;
    this.potholes.setFill(p, fill01);
    this.session.recompute();
    this.session.pinned = true;
    p.fill01 = this.session.fill01;
    p.visualDirty = true;
  }

  endSession(reason, emit) {
    if (!this.session.active) return;
    if (this._completeHold > 0) { this._completeHold = 0; this.session.release(); return; }
    const p = this.potholes.byId(this.session.potholeId);
    if (p && p.state === POTHOLE_STATE.REPAIRING) {
      p.state = POTHOLE_STATE.OPEN;
      p.visualDirty = true;
    }
    const id = this.session.potholeId;
    this.session.release();
    if (emit && id >= 0) this.ctx.bus.emit(EV.REPAIR_FAILED, { id, reason });
  }

  /**
   * The payoff. TIME first — the countdown jumping is the whole reason the loop works —
   * then cash, then score, then the combo, then a cone dropped on the fresh patch.
   */
  completeRepair(p) {
    const ctx = this.ctx;
    const P = this.T.payout;
    const C = this.T.combo;

    const quality01 = this.model.quality(this.session);
    const perfect = this.model.isPerfect(quality01);
    const durationMs = Math.round((ctx.time.sim - this._sessionStartSim) * 1000);

    const combo = this.score.combo + 1;
    const comboMul = comboMultiplier(combo, C);
    const slalomMul = 1 + Math.min(this.score.slalomChain, this.T.slalom.chainCap) * this.T.slalom.perLink;
    const payout = repairPayout(quality01, comboMul, slalomMul, perfect, P);

    this.potholes.complete(p, quality01, perfect);
    // Hold the session at a full meter rather than releasing it immediately — see
    // TUNING.repair.completeHold. cells are all 1 now, so recompute pins fill01 to 1.
    this.session.recompute();
    this._completeHold = this.T.repair.completeHold;
    this.lastCompleted = p;
    this.score.repaired++;
    if (perfect) this.score.perfects++;

    // TIME.
    const maxT = ctx.config.round.maxSeconds;
    const before = this.timeRemaining;
    this.timeRemaining = Math.min(maxT, this.timeRemaining + payout.seconds);
    const granted = Math.round((this.timeRemaining - before) * 10) / 10;
    this.score.secondsEarned += granted;

    /**
     * Anything the ceiling refused becomes score instead of vanishing. An eight-repair chain
     * used to peg the timer at exactly maxSeconds every time and every second past it was
     * silently thrown away, so the economy went flat at the exact moment the player was doing
     * best. See TUNING.payout.overflowScorePerSecond.
     */
    const overflow = Math.max(0, payout.seconds - (this.timeRemaining - before));
    const overflowScore = overflow > 0 ? Math.round(overflow * P.overflowScorePerSecond) : 0;

    this.score.cash += payout.cash;
    this.score.score += payout.score + overflowScore;

    // COMBO.
    this.score.combo = combo;
    this.score.multiplier = comboMul;
    if (combo > this.score.bestCombo) this.score.bestCombo = combo;
    this._comboTimer = 0;

    ctx.bus.emit(EV.REPAIR_COMPLETED, {
      id: p.id, position: p.position, quality01, perfect,
      timeAwarded: granted, cash: payout.cash, durationMs,
    });
    ctx.bus.emit(EV.GAME_TIME_EXTENDED, {
      seconds: granted, reason: perfect ? 'perfect' : 'repair', worldPos: p.position,
      // Optional, additive: seconds the ceiling refused, and what they were worth instead.
      // `ui` may show it or ignore it; nothing downstream is required to read it.
      overflowSeconds: Math.round(overflow * 10) / 10, overflowScore,
    });
    ctx.bus.emit(EV.COMBO_INCREASED, { combo, multiplier: comboMul, worldPos: p.position });

    const tier = comboTier(combo, C);
    if (tier > this._lastTier) {
      this._lastTier = tier;
      ctx.bus.emit(EV.COMBO_TIER_REACHED, { tier, combo });
      ctx.audio.announcer('combo' + tier, 2);
      this.announce(`COMBO ×${combo}`, 'combo', 1800);
    }

    // Bank and clear the slalom chain — it paid into this repair, it does not carry.
    if (this.score.slalomChain > 0) {
      this.score.slalomChain = 0;
      this.slalomChainTimer = 0;
    }

    ctx.fx.popText(p.position, `+${granted.toFixed(0)}s`, perfect ? 'perfect' : 'time');
    ctx.fx.hitFlash(perfect ? 0xffe27a : 0xffffff, perfect ? 0.5 : 0.28);
    ctx.fx.freezeFrame(perfect ? 60 : 32);
    ctx.audio.sfx('repairComplete', { pos: p.position, rate: perfect ? 1.12 : 1 });
    ctx.audio.announcer(perfect ? 'perfect' : 'timeExtend', perfect ? 3 : 1);
    this.announce(perfect ? 'PARFAIT!' : `+${granted.toFixed(0)} SECONDES`, perfect ? 'perfect' : 'timeExtend', 1600);

    // Marker ring comes down, guard cone goes on.
    this.retireRing(p);
    this._deployGuard(p);

    this.retargetNow();
  }

  _deployGuard(p) {
    const ctx = this.ctx;
    let dx = ctx.player.forward ? -ctx.player.forward.x : 0;
    let dz = ctx.player.forward ? -ctx.player.forward.z : -1;
    const l = Math.sqrt(dx * dx + dz * dz) || 1;
    dx /= l; dz /= l;
    const c = this.cones.deploy(p, dx, dz, this.rng);
    if (!c) return;
    ctx.bus.emit(EV.CONE_DEPLOYED, { id: c.id, position: c.position, potholeId: p.id });
    ctx.audio.sfx('coneDeploy', { pos: c.position, gain: 0.5 });
  }

  // ------------------------------------------------------------------ combo

  _stepComboWindow(dt) {
    if (this.score.combo <= 0) return;
    this._comboTimer += dt;
    if (this._comboTimer >= this.T.combo.windowSeconds) this._breakCombo('timeout');
  }

  _breakCombo(reason) {
    if (this.score.combo <= 0) { this._comboTimer = 0; return; }
    const combo = this.score.combo;
    this.score.combo = 0;
    this.score.multiplier = 1;
    this._comboTimer = 0;
    this._lastTier = 0;
    this.ctx.bus.emit(EV.COMBO_BROKEN, { combo, reason });
    if (combo >= 3) {
      this.ctx.audio.announcer('comboLost', 2);
      this.announce('COMBO PERDU', 'comboBroken', 1300);
    }
  }

  _onVehicleCollided(p) {
    if (this.phase !== PHASE.PLAYING) return;
    if (!p) return;
    if (p.other === 'cone') return;                    // cones have their own rules
    if ((p.impulse || 0) < this.T.combo.crashImpulse) return;
    this._breakCombo('crash');
  }

  // ------------------------------------------------------------------ phases

  _setPhase(next, silent) {
    if (this.phase === next) return;
    const from = this.phase;
    this.phase = next;
    this.phaseTime = 0;

    if (!silent) this.ctx.bus.emit(EV.GAME_PHASE_CHANGED, { from, to: next });

    if (next === PHASE.PLAYING && !this._startedSent) {
      this._startedSent = true;
      this.ctx.bus.emit(EV.GAME_STARTED, {
        seed: this.ctx.seed, roundIndex: this.roundIndex,
        weather: this.weather, potholeCount: this.potholes.count,
      });
      this.ctx.audio.music('start');
    }
    if (next === PHASE.TIMEUP) {
      this.ctx.audio.announcer('timeUp', 3);
      this.announce('TEMPS ÉCOULÉ', 'timeup', 2200);
    }
    if (next === PHASE.RESULT) {
      this._replayArmed = false;
      this._emitGameOver();
    }
  }

  _emitGameOver() {
    if (this._gameOverSent) return;
    this._gameOverSent = true;
    const o = this._resultOverride;
    const R = this.T.round;
    const repaired = this.score.repaired;
    const total = this._totalOverride || this.potholes.count;
    const completion01 = total > 0 ? clamp01(repaired / total) : 0;

    /**
     * ONE NUMBER. A forced rank from the harness is still passed through the completion gate:
     * the RESULT screen draws its star row from `repaired / total`, so a letter that is not
     * monotone in the same quantity puts an S badge beside 4 of 5 stars and reads as a
     * rendering bug (round-6 defect). Capping is not overriding the shot — the shot asks for
     * a strong result on a 21-of-24 board, and A + 4 stars IS that result, stated
     * consistently. Repair all 24 and the same code emits S + 5.
     */
    const rank = capRankByCompletion((o && o.rank) || rankFor(this.score.score, R, completion01),
      completion01, R);

    const payload = {
      score: this.score.score,
      cash: Math.round(this.score.cash * 100) / 100,
      repaired,
      total,
      rank,
      /** Published so `ui` can bind badge and stars to a single source rather than two. */
      stars: starsForRank(rank, R.starCount),
      starCount: R.starCount,
      completion01: Math.round(completion01 * 1000) / 1000,
      /** Rubric `mission-complete` criterion 8: the panel must NAME the street. */
      street: this._resultStreet(),
      perfects: (o && o.perfects !== undefined) ? o.perfects : this.score.perfects,
      bestCombo: (o && o.bestCombo !== undefined) ? o.bestCombo : this.score.bestCombo,
    };
    this.ctx.bus.emit(EV.GAME_OVER, payload);
    this.ctx.audio.music('stop');
  }

  /**
   * The street the round was actually fought on. Rubric `mission-complete` criterion 8 wants
   * "the named object or street in plain readable text at the top of the panel", and the
   * round-6 critic correctly pointed out that RUE RÉPARÉE is a category, not a name. The
   * names already exist — every hole carries the `world.landmarkNear` result — so this is the
   * modal name over the holes that were repaired, falling back to the whole field and then to
   * the landmark under the player.
   */
  _resultStreet() {
    let best = null, bestN = 0;
    const counts = new Map();
    for (let pass = 0; pass < 2 && !best; pass++) {
      counts.clear();
      for (let i = 0; i < this.potholes.count; i++) {
        const p = this.potholes.items[i];
        if (!p.active || !p.name) continue;
        if (pass === 0 && p.state === POTHOLE_STATE.OPEN) continue;
        const n = (counts.get(p.name) || 0) + 1;
        counts.set(p.name, n);
        if (n > bestN) { bestN = n; best = p.name; }
      }
    }
    if (best) return best;
    const lm = this.ctx.world.landmarkNear(this.ctx.player.position.x, this.ctx.player.position.z);
    return (lm && lm.name) || LANDMARKS[0];
  }

  announce(text, style, durationMs) {
    this.ctx.bus.emit(EV.UI_ANNOUNCE, { text, style, durationMs: durationMs || 1500 });
  }

  setWeather(w, transitionMs) {
    if (!w || w === this.weather) return;
    this.weather = w;
    this.ctx.bus.emit(EV.WORLD_WEATHER_CHANGED, { weather: w, transitionMs: transitionMs != null ? transitionMs : 800 });
    this.ctx.audio.setAmbience(w, 1);
  }

  /**
   * May we choose the weather ourselves?
   *
   * No, in exactly two cases. A harness build must stay on the documented 'thaw' default so
   * `reset({seed})` + a shot's own `setState({weather})` produce byte-identical layouts
   * (§7.3) — the layout key includes the weather, so a random draw there would reshuffle
   * every graded frame. And once anything has explicitly set a weather through the harness,
   * that choice is the authority until the next reset; a round rollover must not clobber it.
   */
  _autoWeather() {
    return !this.ctx.isHarness && !this._weatherForced;
  }

  /**
   * This round's weather, or null if we cannot draw one yet.
   *
   * `Rng.fork` derives from (seed, label) and consumes nothing from its parent, so this draw
   * is invisible to every other gameplay stream — the layout for a given (seed, round) is
   * bit-identical whether or not this ran. Keying on `roundIndex` is what makes a restart
   * differ: the run seed is fixed at boot (core/game.js), so the round counter is the only
   * thing that moves between playthroughs of one page load.
   */
  _pickWeather(roundIndex) {
    const src = this.rng || (this.ctx.rng ? this.ctx.rng.fork('gameplay') : null);
    if (!src) return null;
    const W = this.T.weather;
    return src.fork(`weather:round:${roundIndex}`).pickWeighted(W.pool, W.weights);
  }

  /** Draw and apply the weather for the current round. No-op under harness authority. */
  _rollRoundWeather(transitionMs) {
    if (!this._autoWeather()) return;
    this.setWeather(this._pickWeather(this.roundIndex), transitionMs);
  }

  // ================================================================== presentation

  /**
   * Presentation only. Everything here is a pure function of simulation state plus
   * ctx.time.sim, which is what makes freezeClocks() shots bit-identical (§7.3).
   *
   * NOTE: `gameplay` is absent from core's UPDATE_ORDER (src/core/registry.js), so this is
   * currently never called by the loop. `_syncVisuals()` is therefore also driven off the
   * tail of fixedUpdate(); it is idempotent and no-ops when nothing is dirty, so having both
   * paths costs nothing and the subsystem keeps working either way. Raised with the lead —
   * once `gameplay` is added to UPDATE_ORDER the fixedUpdate call becomes a cheap no-op.
   */
  update(dt, alpha) {
    this._syncVisuals();
  }

  /**
   * Push simulation state into the meshes. Idempotent, allocation-free, and cheap when
   * nothing has changed: refreshDirty walks 44 booleans and writeInstances walks 320.
   */
  _syncVisuals() {
    this.potholes.refreshDirty();
    this.cones.writeInstances(false);
    this._updateReticle();
    this._updateTargetRing();
  }

  _updateReticle() {
    const s = this.session;
    if (!s.active) { this.reticle.visible = false; return; }
    const p = this.potholes.byId(s.potholeId);
    if (!p) { this.reticle.visible = false; return; }

    this._nozzleWorld(p, this._tmp);
    const T = this.T.repair;
    const r = (T.sprayRadius + (T.sprayRadiusFocused - T.sprayRadius) * s.focus) * p.radius;
    const pulse = 1 + 0.08 * Math.sin(this.ctx.time.sim * this.T.visual.reticlePulseHz * Math.PI * 2);
    this.reticle.position.set(this._tmp.x, this._tmp.y + 0.035, this._tmp.z);
    this.reticle.scale.setScalar(r * pulse);
    this.reticle.visible = true;
  }

  _updateTargetRing() {
    /**
     * The objective ring is a "GO HERE" marker for a player doing 90 km/h two blocks away.
     * Once you are stood on the hole with the boom down it is redundant, and at the 3.2 m
     * `pothole-fill` camera a 2.6 × radius ring is a yellow donut across the whole frame that
     * out-shouts the repair it is pointing at. Drop it the moment the session binds.
     */
    if (this.session.active) { this.targetRing.visible = false; return; }
    const p = this.potholes.byId(this.targetId);
    if (!p || !p.active || !p.isOpen) { this.targetRing.visible = false; return; }
    const T = this.T.target;
    const pulse = 1 + 0.10 * Math.sin(this.ctx.time.sim * T.ringPulseHz * Math.PI * 2);
    this.targetRing.position.set(p.position.x, p.groundY + 0.02, p.position.z);
    this.targetRing.scale.setScalar(p.radius * T.ringRadiusFactor * pulse);
    this.targetRing.visible = true;
  }

  onResize() {}

  // ================================================================== harness

  /** ARCHITECTURE.md §7.1. Keys are applied in a fixed order — scenario before session. */
  applyHarnessState(patch) {
    if (!patch) return;

    // An explicit weather is authoritative until the next reset() — see _autoWeather().
    if (patch.weather) { this._weatherForced = true; this.setWeather(patch.weather); }

    if (patch.scenario !== undefined && patch.scenario !== this.scenario) {
      this.scenario = patch.scenario;
      applyScenario(this, patch.scenario);
    }

    if (patch.timeRemaining !== undefined) this.timeRemaining = patch.timeRemaining;
    if (patch.score !== undefined) this.score.score = patch.score;
    if (patch.cash !== undefined) this.score.cash = patch.cash;
    if (patch.repaired !== undefined) this.score.repaired = patch.repaired;
    if (patch.total !== undefined) this._totalOverride = patch.total;
    if (patch.combo !== undefined) {
      this.score.combo = patch.combo;
      this.score.multiplier = comboMultiplier(patch.combo, this.T.combo);
      if (patch.combo > this.score.bestCombo) this.score.bestCombo = patch.combo;
      this._lastTier = comboTier(patch.combo, this.T.combo);
    }
    if (patch.multiplier !== undefined) this.score.multiplier = patch.multiplier;

    if (patch.result) {
      this._resultOverride = patch.result;
      if (patch.result.bestCombo !== undefined) this.score.bestCombo = patch.result.bestCombo;
      if (patch.result.perfects !== undefined) this.score.perfects = patch.result.perfects;
    }

    if (patch.phase !== undefined && patch.phase !== this.phase) {
      // Forced phases are absolute — no re-entry guard, the harness owns the clock here.
      const from = this.phase;
      this.phase = patch.phase;
      this.phaseTime = 0;
      if (patch.phase === PHASE.PLAYING) this._startedSent = true;
      if (patch.phase === PHASE.RESULT) { this._gameOverSent = false; this._emitGameOver(); }
      this.ctx.bus.emit(EV.GAME_PHASE_CHANGED, { from, to: patch.phase });
    }

    if (patch.repairSession !== undefined) {
      const rs = patch.repairSession;
      if (!rs || rs.active === false) {
        this.endSession('harness', false);
      } else {
        let p = rs.potholeId !== undefined && rs.potholeId !== null
          ? this.potholes.byId(rs.potholeId)
          : this.potholes.byId(this.targetId);
        if (!p) p = this._nearestOpen();
        if (p) {
          if (!this.session.active || this.session.potholeId !== p.id) {
            this.endSession('harness', false);
            this.beginSession(p, true);
          }
          this.pinSession(rs.fill01 !== undefined ? rs.fill01 : this.session.fill01);
        }
      }
    }

    if (patch.announce && patch.announce.text) {
      this.announce(patch.announce.text, patch.announce.style || 'info', patch.announce.durationMs || 2000);
    }

    this.cones.markDirty();
    this._syncVisuals();
  }

  _nearestOpen() {
    for (let i = 0; i < this.potholes.count; i++) {
      const p = this.potholes.items[i];
      if (p.active && p.isOpen) return p;
    }
    return null;
  }

  harnessStats() {
    let open = 0, fresh = 0, cured = 0, standing = 0;
    for (let i = 0; i < this.potholes.count; i++) {
      const p = this.potholes.items[i];
      if (!p.active) continue;
      if (p.state === POTHOLE_STATE.CURED) cured++;
      else if (p.state === POTHOLE_STATE.FRESH) fresh++;
      else open++;
    }
    for (let i = 0; i < this.cones.count; i++) {
      const c = this.cones.items[i];
      if (c.active && c.state === CONE_STATE.STANDING) standing++;
    }
    return {
      phase: this.phase,
      scenario: this.scenario,
      weather: this.weather,
      timeRemaining: Math.round(this.timeRemaining * 100) / 100,
      potholes: this.potholes.count,
      open, fresh, cured,
      cones: this.cones.count,
      conesStanding: standing,
      gates: this.cones.gateCount,
      cells: this.model.grid.count,
      score: this.score.score,
      cash: Math.round(this.score.cash * 100) / 100,
      combo: this.score.combo,
      multiplier: Math.round(this.score.multiplier * 1000) / 1000,
      repaired: this.score.repaired,
      perfects: this.score.perfects,
      bestCombo: this.score.bestCombo,
      slalomChain: this.score.slalomChain,
      thumps: this.score.thumps,
      sessionActive: this.session.active,
      fill01: Math.round(this.session.fill01 * 10000) / 10000,
      minCell: Math.round(this.session.minCell * 10000) / 10000,
      targetId: this.targetId,
      targetName: this.activeTarget ? this.activeTarget.name : null,
      roundIndex: this.roundIndex,
    };
  }

  /**
   * §7.3. Nothing in this subsystem reads a wall clock in the first place — every animated
   * value is a function of ctx.time.sim or of simulation state — so this only has to force
   * a full geometry rewrite so no slot is left stale from a previous configuration.
   */
  freezeClocks() {
    this._frozen = true;
    this.potholes.refreshAll();
    this.cones.writeInstances(true);
    this._updateReticle();
    this._updateTargetRing();
  }

  async reset(opts) {
    const o = opts || {};
    this.rng = this.ctx.rng.fork('gameplay');
    this.session = new RepairSession(this.model.grid);
    resetScore(this.score);
    this.timeRemaining = this.ctx.config.round.startSeconds;
    this.scenario = 'none';
    this.roundIndex = Number.isInteger(o.roundIndex) ? Math.max(0, o.roundIndex) : 0;
    // A reset is a clean slate, so a previously forced weather stops being authoritative —
    // unless this reset itself names one. Harness resets keep landing on 'thaw' exactly as
    // before; only a normal-play rebuild re-rolls, and it does so before layoutRound() below.
    this._weatherForced = !!o.weather;
    this.weather = o.weather || 'thaw';
    this.phase = PHASE.TITLE;
    this.phaseTime = 0;
    this._comboTimer = 0;
    this._lastTier = 0;
    this.slalomChainTimer = 0;
    this._resultOverride = null;
    this._totalOverride = 0;
    this._gameOverSent = false;
    this._startedSent = false;
    this._replayArmed = false;
    this._scanTick = this.T.target.rescanSteps;
    this._zoneGrace = 0;
    this._completeHold = 0;
    this._sessionStartSim = 0;
    this._progressCursor = 0;

    this._rollRoundWeather(0);
    this._publishQuery();
    this.layoutRound();
    this.potholes.refreshAll();
    this.cones.writeInstances(true);
  }

  dispose() {
    if (this._onCollided) { this.ctx.bus.off(EV.VEHICLE_COLLIDED, this._onCollided); this._onCollided = null; }
    if (this.potholes) this.potholes.dispose();
    if (this.cones) this.cones.dispose();
    for (const m of [this.reticle, this.targetRing]) {
      if (!m) continue;
      this.ctx.scene.remove(m);
      m.geometry.dispose();
      if (m.material && m.material.dispose) m.material.dispose();
    }
    this.reticle = null;
    this.targetRing = null;
  }
}

// ------------------------------------------------------------------ local helpers

/** Flat annulus in the XZ plane at unit radius, so a uniform mesh scale IS its world radius. */
function annulus(inner, outer, segments) {
  const verts = [];
  const norms = [];
  const uvs = [];
  const idx = [];
  for (let s = 0; s <= segments; s++) {
    const t = s / segments;
    const a = t * Math.PI * 2;
    const c = Math.cos(a), z = Math.sin(a);
    verts.push(c * inner, 0, z * inner, c * outer, 0, z * outer);
    norms.push(0, 1, 0, 0, 1, 0);
    // UVs matter: every marking material is textured, and geometry with no `uv` attribute
    // samples texel (0,0) across the whole surface — which for a soft sprite is a fully
    // transparent ring you will spend an hour failing to find in the frame.
    uvs.push(t * 6, 0, t * 6, 1);
  }
  for (let s = 0; s < segments; s++) {
    const a = s * 2;
    idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(norms, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  return geo;
}

/**
 * Make a private material clone usable as a flat ground marker.
 * `emissive` is the emissive INTENSITY; 0 leaves the mark purely lit by the scene, which is
 * what "painted guidance" means and what keeps it from out-contrasting a near-black crater.
 */
function softMarkerMaterial(mat, opacity, emissive) {
  if ('transparent' in mat) mat.transparent = true;
  if ('opacity' in mat) mat.opacity = opacity;
  if ('depthWrite' in mat) mat.depthWrite = false;
  if ('roughness' in mat) mat.roughness = 0.4;
  if ('side' in mat) mat.side = THREE.DoubleSide;
  // A little emissive so the ring still reads at night without becoming a light source.
  if (mat.emissive && mat.emissive.isColor && mat.color && mat.color.isColor) mat.emissive.copy(mat.color);
  if ('emissiveIntensity' in mat) mat.emissiveIntensity = emissive != null ? emissive : 0.45;
  mat.polygonOffset = true;
  mat.polygonOffsetFactor = -4;
  mat.polygonOffsetUnits = -8;
  mat.needsUpdate = true;
  return mat;
}
